import { Button } from '@vadiun/react-components';
import { Query } from '@vadiun/react-hooks-legacy';
import { ApplicantModel } from 'app/client/clientBusiness/Applicant/models/ApplicantModel';
import { AuthorizedModel } from 'app/client/clientBusiness/Authorized/models/Authorized';
import { PassengerModel } from 'app/client/clientBusiness/Passenger/models/Passenger';
import {
  ClientBusinessModel,
  ClientBusinessPartialModel
} from 'app/client/clientBusiness/models/ClientBusiness';
import { RateModel } from 'app/client/clientBusiness/rates/models/RateModel';
import {
  ErrorMessage,
  Field,
  FieldArray,
  Form,
  Formik,
  FormikProps
} from 'formik';
import { KeyboardDateTimePicker } from 'formik-material-ui-pickers';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import { StaticMap } from 'shared/components';
import { TransferReservationBaseForm } from '../../../viajeTraslado/forms/base/TransferReservationBaseForm';
import { TransferReservationLocationFormModal } from '../../components/TransferReservationLocationFormModal';
import { createTransferReservationSingleFormInitialValues } from './TransferReservationSingleFormInitialValue';
import { TransferReservationSingleFormType } from './TransferReservationSingleFormType';
import { transferReservationSingleFormValidation } from './TransferReservationSingleFormValidation';
import { BusType } from '../../models/TransferReservation';
import { useSnackbar } from 'notistack';
import VehicleSingleForm from './VehicleSingleForm';
import { CheckboxWithLabel } from 'formik-material-ui';
import { ViajeTrasladoTipoServicio } from '../../models';
import useEventoService from 'app/eventos/services/useEventoService';
import { EventModel } from 'app/eventos/models/Evento';

interface Props {
  initialValues?: TransferReservationSingleFormType;
  onSubmit: (viajeFormValues: TransferReservationSingleFormType, reset) => void;
  getClientesByName: (name: string) => Promise<ClientBusinessPartialModel[]>;
  onClienteSelect: (cliente: ClientBusinessPartialModel) => void;
  solicitantes: ApplicantModel[];
  pasajeros: PassengerModel[];
  autorizadosQuery: Query<AuthorizedModel[]>;
  eventsQuery: Query<EventModel[]>;
  clientBusiness: ClientBusinessModel | undefined;
  fixedRates?: RateModel['categories'][0]['fixedPrices'];
  onCalculateDistanceAndCost: (
    viajeFormValues: TransferReservationSingleFormType
  ) => void;
  optionsBuses?: BusType[];
  isValidation?: boolean;
}

export function TransferReservationSingleForm({
  initialValues: _initialValues,
  onSubmit,
  getClientesByName,
  solicitantes,
  pasajeros,
  autorizadosQuery,
  eventsQuery,
  onClienteSelect,
  clientBusiness,
  onCalculateDistanceAndCost,
  fixedRates,
  optionsBuses
}: Props) {
  const initialValues = useMemo(
    () => _initialValues ?? createTransferReservationSingleFormInitialValues(),
    [_initialValues]
  );
  const formRef = useRef<FormikProps<TransferReservationSingleFormType>>(null);
  const firstLoad = useRef(true);

  const { getEventActives } = useEventoService();


  useEffect(() => {
    if (clientBusiness === undefined) return;
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    formRef.current?.setFieldValue('prioridad', clientBusiness.hasPriority);
    formRef.current?.setFieldValue('autorizado', undefined);
  }, [clientBusiness]);

  const checkMinutesBeforeDeparture = (viaje) => {
    return moment(viaje.fechaYHoraDeLlegada).isBefore(
      moment(viaje.minutosPreviosASalida)
    );
  };

  const { enqueueSnackbar } = useSnackbar();

  const isPast = (viaje) =>
    moment(viaje.fechaYHoraDeLlegada).isBefore(
      moment().subtract(15, 'minutes')
    );

  return (
    <Formik<TransferReservationSingleFormType>
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={transferReservationSingleFormValidation(
        clientBusiness,
        formRef.current?.values.isLLTPais ?? false
      )}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {(formik) => (
        <Form className="w-full">
          <TransferReservationBaseForm
            {...formik}
            getClientesByName={getClientesByName}
            solicitantes={solicitantes}
            autorizadosQuery={autorizadosQuery}
            eventsQuery={eventsQuery}
            onClienteSelect={onClienteSelect}
            clientBusiness={clientBusiness}
            isValidation
          />
          <div className="mt-8 flex space-x-8">
            <div className="flex-grow">
              <VehicleSingleForm
                {...formik}
                autorizadosQuery={autorizadosQuery}
                clientBusiness={clientBusiness}
                fixedRates={fixedRates}
                optionsBuses={optionsBuses}
              />
              <div className="flex space-x-8">
                <div className="col-span-2">
                  <Field
                    component={CheckboxWithLabel}
                    type="checkbox"
                    name={`prioridad`}
                    color="primary"
                    Label={{ label: 'Prioritario' }}
                    fullWidth
                  />
                </div>
                <div className="col-span-2">
                  <Field
                    component={CheckboxWithLabel}
                    type="checkbox"
                    name={`hasOutSite`}
                    color="primary"
                    Label={{ label: 'Out Site' }}
                  />
                </div>
              </div>
              <div className="flex space-x-8">
                <Field
                  component={KeyboardDateTimePicker}
                  label="Fecha de llegada"
                  name={`fechaYHoraDeLlegada`}
                  ampm={false}
                  inputVariant="outlined"
                  margin="normal"
                  className="flex-1"
                  format="DD/MM/YYYY HH:mm"
                />

                <div className="flex flex-1 flex-col">
                  <Field
                    component={KeyboardDateTimePicker}
                    label="Tiempo previo salida"
                    name={`minutosPreviosASalida`}
                    ampm={false}
                    error={checkMinutesBeforeDeparture(formik.values)}
                    inputVariant="outlined"
                    margin="normal"
                    className="flex-1"
                    format="DD/MM/YYYY HH:mm"
                  />
                  {checkMinutesBeforeDeparture(formik.values) && (
                    <p className="m-0 text-sm text-red-500">
                      La hora de salida debe ser anterior a la hora de llegada
                    </p>
                  )}
                </div>
              </div>

              <FieldArray
                name={`puntos`}
                render={() => (
                  <TransferReservationLocationFormModal
                    pasajeros={pasajeros}
                    puntos={formik.values.puntos}
                    onEditPuntos={(puntos) => {
                      formik.setFieldValue(`puntos`, puntos);
                    }}
                  />
                )}
              />
              <ErrorMessage name={`puntos`}>
                {(msg) => (
                  <p className="mt-4 text-sm text-red-500">
                    {JSON.stringify(msg)}
                  </p>
                )}
              </ErrorMessage>
            </div>
            <div
              className={`w-1/4 transition-all delay-300 duration-300 ease-in-out hover:w-2/5 ${formik.values.puntos.length === 0 ? 'opacity-0' : 'opactiy-1'
                }`}
            >
              <StaticMap
                markers={formik.values.puntos.map((punto) => ({
                  lat: punto.domicilio.latitud,
                  lng: punto.domicilio.longitud
                }))}
                size="600x600"
              />
            </div>
          </div>
          {isPast(formik.values) && (
            <div className="mt-4 bg-red-100 border border-red-500 p-4 rounded">
              <p className="text-red-700 text-sm mb-2">
                ⚠️ Este viaje está siendo editado con una fecha anterior a la
                actual. Ingresá el motivo para continuar:
              </p>

              <Field
                name="reason_reserve_past"
                as="input"
                placeholder="Ejemplo: el cliente avisó tarde"
                className={`w-full p-2 border rounded text-sm ${formik.touched.reason_reserve_past &&
                  formik.errors.reason_reserve_past
                  ? 'border-red-500'
                  : 'border-gray-300'
                  }`}
              />

              <ErrorMessage name="reason_reserve_past">
                {(msg) => <p className="text-sm text-red-600 mt-1">{msg}</p>}
              </ErrorMessage>
            </div>
          )}

          <div className="mt-8 flex justify-between">
            <Button
              variant="contained"
              color="primary"
              disabled={formik.values.puntos.length < 2 || formik.values.tipoServicio === ViajeTrasladoTipoServicio.SERVICIOS_GRUPALES}
              onClick={() => onCalculateDistanceAndCost(formik.values)}
            >
              Calcular costos y distancias
            </Button>

            <Button
              variant="contained"
              color="green"
              className="ml-auto"
              onClick={() => {
                const motiveIncomplete =
                  !formik.values.reason_reserve_past?.trim();

                if (motiveIncomplete && isPast(formik.values)) {
                  enqueueSnackbar(
                    'Debés completar el motivo para poder crear un viaje en el pasado.',
                    {
                      variant: 'error'
                    }
                  );
                  return;
                }

                formik.submitForm();
              }}
            >
              Guardar Viaje
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
